import React, { useEffect, useState, useCallback } from 'react';
import { Button, Card, DatePicker, Form, Input, message, Space } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Service } from '@/apis';
import type { NewsAddDto, NewsUpdateDto } from '@/apis';
import { MultiImageUpload } from '@/page/Cms/ProductManagement/productShared';
import RichTextEditor from '@/components/RichTextEditor';
import styles from '@/components/CommonTable/index.module.less';
import formStyles from './NewsForm.module.less';

type NewsRow = {
  id: number;
  title?: string;
  summary?: string;
  body?: string;
  publishedAt?: string;
  coverImage?: string;
};

const NewsForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!isEdit || !id) return;
    setDetailLoading(true);
    Service.ycNewsControllerGetDetail(id)
      .then((res: unknown) => {
        const raw = (res as { data?: unknown })?.data as Record<string, unknown> | undefined;
        const data = (raw?.data as Record<string, unknown>) || raw;
        if (!data) return;
        const row: NewsRow = {
          ...data,
          id: (data.id as number) ?? Number(id),
        } as NewsRow;
        form.setFieldsValue({
          title: row.title,
          summary: row.summary,
          body: row.body,
          publishedAt: row.publishedAt ? dayjs(row.publishedAt as string) : undefined,
          coverImage: row.coverImage,
        });
      })
      .catch(() => message.error('获取新闻详情失败'))
      .finally(() => setDetailLoading(false));
  }, [id, isEdit, form]);

  const onFinish = async (values: Record<string, unknown>) => {
    const publishedAt = values.publishedAt != null && dayjs.isDayjs(values.publishedAt)
      ? (values.publishedAt as dayjs.Dayjs).toISOString()
      : (values.publishedAt as string | undefined);

    setLoading(true);
    try {
      if (isEdit && id) {
        const payload: NewsUpdateDto = {
          id: Number(id),
          title: values.title as string,
          summary: values.summary as string,
          body: values.body as string,
          publishedAt,
          coverImage: values.coverImage as string,
        };
        await Service.ycNewsControllerPut(payload);
        message.success('更新成功');
      } else {
        const payload: NewsAddDto = {
          title: values.title as string,
          summary: values.summary as string,
          body: values.body as string,
          publishedAt,
          coverImage: values.coverImage as string,
        };
        await Service.ycNewsControllerAdd(payload);
        message.success('创建成功');
      }
      navigate('/cms/news');
    } catch {
      message.error(isEdit ? '更新失败' : '创建失败');
    } finally {
      setLoading(false);
    }
  };

  const goBack = useCallback(() => {
    navigate('/cms/news');
  }, [navigate]);

  return (
    <div className={styles.dataTablePage}>
      <div className={formStyles.newsFormHeader}>
        <div className={formStyles.newsFormHeaderLeft}>
          <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
            返回
          </Button>
          <span className={formStyles.newsFormTitle}>
            {isEdit ? '编辑新闻' : '新增新闻'}
          </span>
        </div>
        <Space>
          <Button
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            {isEdit ? '保存' : '创建'}
          </Button>
          <Button onClick={goBack}>取消</Button>
        </Space>
      </div>
      <div className={formStyles.cardWrap}>
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            disabled={detailLoading}
            className={formStyles.newsForm}
          >
            <div className={formStyles.formRow2col}>
              <Form.Item
                name="title"
                label="标题"
                rules={[{ required: true, message: '请输入标题' }]}
              >
                <Input placeholder="新闻标题" />
              </Form.Item>
              <Form.Item name="publishedAt" label="发布时间">
                <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} placeholder="选择发布时间" />
              </Form.Item>
            </div>
            <div className={formStyles.formItemFull}>
              <Form.Item name="summary" label="摘要">
                <Input.TextArea rows={2} placeholder="简短摘要" />
              </Form.Item>
            </div>
            <div className={formStyles.formItemFull}>
              <Form.Item
                name="coverImage"
                label="封面图"
                getValueFromEvent={(urls: string[]) => urls?.[0]}
                getValueProps={(v: string) => ({ value: v ? [v] : [] })}
              >
                <MultiImageUpload maxCount={1} />
              </Form.Item>
            </div>
            <div className={formStyles.formItemFull}>
              <Form.Item name="body" label="正文">
                <RichTextEditor
                  placeholder="正文内容，支持富文本"
                  minHeight={300}
                  autoHeight
                />
              </Form.Item>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default NewsForm;
